import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./FormCreateCompany.scss";
import Button from "../../Button/Button";
import FormInput from "../FormInput/FormInput";
import { useNavigate } from "react-router-dom";
import {
  BUSINESS_SECTOR,
  CompanyResponse,
  N_OF_EMPLOYEES,
  SubscriptionPeriod,
} from "../../../models/CompaniesModel";
import { useForm, useFormState, useWatch } from "react-hook-form";
import FormSelect from "../FormInput/FormSelect/FormSelect";
import { sectors, nOfEmployees } from "../../../models/CompaniesModel";
import Skeleton from "react-loading-skeleton";
import {
  LANGUAGE_TRANSLATION,
  translations,
} from "../../../models/SurveyModels";
import CanoneSection from "./CanoneSection";
interface IForm {
  isLoading: boolean;
  companyData?: CompanyResponse;
  onSumbitForm: (data: CompanyResponse) => void;
}

const FormCreateCompany: React.FC<IForm> = ({
  companyData,
  onSumbitForm,
  isLoading,
}) => {
  const [countNewSeat, setCountNewSeat] = useState(0);
  const [subscriptions, setSubscriptions] = useState<SubscriptionPeriod[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);

  const incrementCountSeat = () => {
    setCountNewSeat(countNewSeat + 1);
  };

  const { register, handleSubmit, control, setValue, clearErrors } =
    useForm<CompanyResponse>({
      mode: "all",
    });

  const formState = useFormState({
    control,
  });

  useEffect(() => {
    if (companyData) {
      setValue("name", companyData.name);
      setValue("headOfficeCity", companyData.headOfficeCity);
      setValue("headOfficeRegion", companyData.headOfficeRegion);
      setValue("vatNumber", companyData.vatNumber);
      setValue("nOfEmployees", companyData.nOfEmployees);
      setValue("businessSector", companyData.businessSector);
      companyData.otherOfficies
        ? setCountNewSeat(companyData.otherOfficies.length)
        : setCountNewSeat(0);

      companyData.otherOfficies &&
        companyData.otherOfficies.forEach((office, index) => {
          setValue(`otherOfficies.${index}.city`, office.city);
          setValue(`otherOfficies.${index}.region`, office.region);
        });

      setValue("hrName", companyData.hrName);
      setValue("hrSurname", companyData.hrSurname);
      setValue("hrEmail", companyData.hrEmail);
      setValue("hrLanguage", companyData.hrLanguage);
      setValue("hrPhone", companyData.hrPhone);
      setSubscriptions(companyData.subscriptions ?? []);
      setCcEmails(companyData.subscriptionCcEmails ?? []);
    }
  }, [companyData, setValue]);

  const onSubmit = (data: CompanyResponse) => {
    if (!data.otherOfficies) data.otherOfficies = [];
    data.subscriptions = subscriptions;
    data.subscriptionCcEmails = ccEmails;
    onSumbitForm(data);
  };

  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <div className="form-container">
        <form className="form-content">
          <div className="row single-section">
            <div className="col-xxl-undefined form-content">
              <div className="row position-relative">
                {isLoading ? (
                  <Skeleton
                    count={3}
                    style={{ marginBottom: "2rem" }}
                    inline
                    height={44}
                    containerClassName="container-radio-skeleton"
                  />
                ) : (
                  <>
                    <FormInput
                      required
                      typeErrors={formState.errors.name?.message as string}
                      isInvalid={formState.errors.hasOwnProperty("name")}
                      label={t(
                        "createAgency.companyData.fields.businessName.label"
                      )}
                      placeholder={t(
                        "createAgency.companyData.fields.businessName.placeholder"
                      )}
                      register={register("name", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                    />
                    <FormInput
                      typeErrors={formState.errors.vatNumber?.message as string}
                      required
                      isInvalid={formState.errors.hasOwnProperty("vatNumber")}
                      defaultValue={companyData?.vatNumber}
                      label={t(
                        "createAgency.companyData.fields.vatNumber.label"
                      )}
                      placeholder={t(
                        "createAgency.companyData.fields.vatNumber.placeholder"
                      )}
                      register={register("vatNumber", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                        pattern: {
                          value: /^\d{11}$/i,
                          message: t("form-generic.error.vat"),
                        },
                      })}
                    />
                    <FormSelect
                      required
                      typeErrors={
                        formState.errors.businessSector?.message as string
                      }
                      isInvalid={formState.errors.hasOwnProperty(
                        "businessSector"
                      )}
                      defaultValue={companyData?.businessSector}
                      options={sectors}
                      label={t("createAgency.companyData.fields.sector.label")}
                      placeholder={t(
                        "createAgency.companyData.fields.sector.placeholder"
                      )}
                      {...register("businessSector", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                      onChange={(selected) => {
                        setValue("businessSector", selected as BUSINESS_SECTOR);
                        clearErrors("businessSector");
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="row single-section">
            <h2 className="registry">{t("createAgency.seats.title")}</h2>
            <div className="col-xxl-undefined form-content">
              <div className="row position-relative">
                {isLoading ? (
                  <Skeleton
                    count={3}
                    style={{ marginBottom: "2rem" }}
                    inline
                    height={44}
                    containerClassName="container-radio-skeleton"
                  />
                ) : (
                  <>
                    <FormInput
                      required
                      typeErrors={
                        formState.errors.headOfficeCity?.message as string
                      }
                      isInvalid={formState.errors.hasOwnProperty(
                        "headOfficeCity"
                      )}
                      defaultValue={companyData?.headOfficeCity}
                      label={t("createAgency.seats.fields.headquarters.label")}
                      placeholder={t(
                        "createAgency.seats.fields.headquarters.placeholder"
                      )}
                      register={register("headOfficeCity", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                    />
                    <FormInput
                      required
                      typeErrors={
                        formState.errors.headOfficeRegion?.message as string
                      }
                      isInvalid={formState.errors.hasOwnProperty(
                        "headOfficeRegion"
                      )}
                      defaultValue={companyData?.headOfficeRegion}
                      label={t("createAgency.seats.fields.region.label")}
                      placeholder={t(
                        "createAgency.seats.fields.region.placeholder"
                      )}
                      register={register("headOfficeRegion", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                    />
                    <FormSelect
                      required
                      typeErrors={
                        formState.errors.nOfEmployees?.message as string
                      }
                      isInvalid={formState.errors.hasOwnProperty(
                        "nOfEmployees"
                      )}
                      defaultValue={companyData?.nOfEmployees}
                      options={nOfEmployees}
                      label={t(
                        "createAgency.seats.fields.employessNumber.label"
                      )}
                      placeholder={t(
                        "createAgency.seats.fields.employessNumber.placeholder"
                      )}
                      {...register("nOfEmployees", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                      onChange={(selected) => {
                        setValue("nOfEmployees", selected as N_OF_EMPLOYEES);
                        clearErrors("nOfEmployees");
                      }}
                    />
                  </>
                )}

                {Array.from({ length: countNewSeat }, (_, index) => (
                  <div key={index} className="row">
                    {isLoading ? (
                      <Skeleton
                        count={2}
                        style={{ marginBottom: "2rem" }}
                        inline
                        height={44}
                        containerClassName="container-radio-skeleton seats"
                      />
                    ) : (
                      <>
                        <FormInput
                          isInvalid={formState.errors.hasOwnProperty(
                            `otherOfficies.${index}.city`
                          )}
                          label={
                            t(
                              "createAgency.seats.fields.secondaryHeadquarters.label"
                            ) +
                            (index + 2)
                          }
                          placeholder={t(
                            "createAgency.seats.fields.secondaryHeadquarters.placeholder"
                          )}
                          register={register(`otherOfficies.${index}.city`)}
                        />
                        <FormInput
                          isInvalid={formState.errors.hasOwnProperty(
                            `otherOfficies.${index}.region`
                          )}
                          label={t(
                            "createAgency.seats.fields.secondaryRegion.label"
                          )}
                          placeholder={t(
                            "createAgency.seats.fields.secondaryRegion.placeholder"
                          )}
                          register={register(`otherOfficies.${index}.region`)}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ margin: "-1rem 0 2.5rem 0" }}>
                <Button
                  color="tertiary"
                  noPadding={true}
                  noHover={true}
                  noBorder={true}
                  onClick={() => incrementCountSeat()}
                  className="capitalize-first-letter"
                >
                  <span>{t("createAgency.newSeatLink")}</span>
                </Button>
              </div>
            </div>
          </div>
          <div className="row single-section">
            <h2 className="registry">{t("createAgency.adminData.title")}</h2>
            <div className="col-xxl-undefined form-content">
              <div className="row position-relative">
                {isLoading ? (
                  <Skeleton
                    count={4}
                    width="32%"
                    style={{ marginBottom: "2rem" }}
                    inline
                    height={44}
                    containerClassName="container-template-skeleton"
                  />
                ) : (
                  <>
                    <FormInput
                      required
                      isInvalid={formState.errors.hasOwnProperty("hrName")}
                                            defaultValue={companyData?.hrName}
                      typeErrors={formState.errors.hrName?.message as string}
                      label={t("createAgency.adminData.fields.adminName.label")}
                      placeholder={t(
                        "createAgency.adminData.fields.adminName.placeholder"
                      )}
                      register={register("hrName", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                    />
                    <FormInput
                      required
                      isInvalid={formState.errors.hasOwnProperty("hrSurname")}
                                            defaultValue={companyData?.hrSurname}
                      typeErrors={formState.errors.hrSurname?.message as string}
                      label={t(
                        "createAgency.adminData.fields.adminSurname.label"
                      )}
                      placeholder={t(
                        "createAgency.adminData.fields.adminSurname.placeholder"
                      )}
                      register={register("hrSurname", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                    />
                    <FormInput
                      required
                      isInvalid={formState.errors.hasOwnProperty("hrEmail")}
                      type="email"
                                            defaultValue={companyData?.hrEmail}
                      label={t("createAgency.adminData.fields.email.label")}
                      typeErrors={formState.errors.hrEmail?.message as string}
                      placeholder={t(
                        "createAgency.adminData.fields.email.placeholder"
                      )}
                      register={register("hrEmail", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t("form-generic.error.email"),
                        },
                      })}
                    />
                    <FormSelect
                      options={translations}
                      required
                      typeErrors={
                        formState.errors?.hrLanguage?.message as string
                      }
                      isInvalid={formState.errors.hasOwnProperty(
                        "hrLanguage"
                      )}
                                            wrapperClass="col-lg-4"
                      label={t(
                        "createQuestion.translations.languageTranslation.hr"
                      )}
                      defaultValue={companyData?.hrLanguage}
                      placeholder={t(
                        "createQuestion.translations.languageTranslation.placeholder"
                      )}
                      {...register("hrLanguage", {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                      onChange={(selected) => {
                        setValue(
                          "hrLanguage",
                          selected as LANGUAGE_TRANSLATION
                        );
                        clearErrors("hrLanguage");
                      }}
                    />
                    <FormInput
                      isInvalid={formState.errors.hasOwnProperty("hrPhone")}
                                            label={t(
                        "createAgency.adminData.fields.phoneNumber.label"
                      )}
                      placeholder={
                        companyData
                          ? ""
                          : t(
                              "createAgency.adminData.fields.phoneNumber.placeholder"
                            )
                      }
                      typeErrors={formState.errors.hrPhone?.message as string}
                      register={register("hrPhone", {
                        pattern: {
                          value:
                            /^\+?([0-9]{1,3}[-. ]?)?\(?[0-9]{3}\)?[-. ]?[0-9]{3}[-. ]?[0-9]{4}$/,
                          message: t("form-generic.error.number"),
                        },
                      })}
                    />
                  </>
                )}

                {Boolean(companyData) && (
                  <div className="col-lg-12">
                    <CanoneSection
                      subscriptions={subscriptions}
                      ccEmails={ccEmails}
                      onChange={({ subscriptions: s, ccEmails: c }) => {
                        setSubscriptions(s);
                        setCcEmails(c);
                      }}
                    />
                  </div>
                )}

                <div className="col-lg-12">
                  <div className="form-action">
                    <div style={{ marginRight: "1rem" }}>
                      <Button
                        color="tertiary"
                        noBorder={true}
                        className="capitalize-first-letter"
                        onClick={() => navigate(-1)}
                      >
                        <span>{t("createAgency.labelCancelButton.edit")}</span>
                      </Button>
                    </div>
                    <Button
                      onClick={handleSubmit(onSubmit)}
                      btnType="submit"
                      className="capitalize-first-letter"
                    >
                      <span>{t("createAgency.labelSubmitButton.edit")}</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default FormCreateCompany;
